import { useState, useEffect, useRef } from "react";
import {
  sendAdminNotification,
  getAdminNotificationHistory,
  searchUsersForNotification,
} from "../../../api/apiServices";

// ── Notification type options ─────────────────────────────────
const TYPES = [
  { value: "system",       label: "📢 Announcement",  desc: "General update or info" },
  { value: "deposit",      label: "💰 Deposit",        desc: "Deposit-related message" },
  { value: "buy",          label: "📈 Buy/Trade",      desc: "Trade-related message" },
  { value: "bill_payment", label: "✅ Bill Payment",   desc: "Bill payment update" },
  { value: "cashback",     label: "🎁 Reward/Cashback",desc: "Reward or cashback update" },
  { value: "streak",       label: "🔥 Streak",         desc: "Streak / activity reminder" },
  { value: "referral",     label: "👥 Referral",       desc: "Referral-related message" },
  { value: "renewal",      label: "📺 Renewal",        desc: "Subscription renewal reminder" },
];

// ── Target options ────────────────────────────────────────────
const TARGETS = [
  { value: "all",      label: "All Users",          icon: "ri-team-line",         color: "#059669" },
  { value: "selected", label: "Selected Users",     icon: "ri-user-search-line",  color: "#0284C7" },
  { value: "kyc",      label: "KYC Approved Users", icon: "ri-shield-check-line", color: "#7C3AED" },
];

// ── User search tag ───────────────────────────────────────────
function UserTag({ user, onRemove }) {
  return (
    <div className="flex items-center gap-1.5 bg-[#00C896]/10 text-[#00C896] text-xs font-semibold px-2.5 py-1 rounded-lg">
      <span>{user.userName || user.email}</span>
      <button onClick={() => onRemove(user.userId)} className="hover:text-red-400 transition-colors">
        <i className="ri-close-line text-xs" />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function NotificationsView() {
  const [tab, setTab] = useState("compose"); // compose | history

  // ── Compose state ─────────────────────────────────────────
  const [form, setForm] = useState({
    title:        "",
    body:         "",
    type:         "system",
    click_action: "/",
    targetType:   "all",
  });
  const [selectedUsers, setSelectedUsers]   = useState([]);
  const [userSearch, setUserSearch]         = useState("");
  const [userResults, setUserResults]       = useState([]);
  const [searchLoading, setSearchLoading]   = useState(false);
  const [sending, setSending]               = useState(false);
  const [sent, setSent]                     = useState(null); // { sent, title }
  const searchRef = useRef(null);

  // ── History state ─────────────────────────────────────────
  const [history, setHistory]       = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // ── Load history when tab switches ───────────────────────
  useEffect(() => {
    if (tab === "history") fetchHistory();
  }, [tab]);

  const fetchHistory = async () => {
    setHistLoading(true);
    try {
      const res = await getAdminNotificationHistory();
      if (res.status) setHistory(res.data);
    } catch { /* silent */ }
    finally { setHistLoading(false); }
  };

  // ── User search ───────────────────────────────────────────
  useEffect(() => {
    if (!userSearch.trim()) { setUserResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await searchUsersForNotification(userSearch);
        if (res.status) {
          // Exclude already-selected
          setUserResults(res.data.filter(u => !selectedUsers.find(s => s.userId === u.userId)));
        }
      } catch { /* silent */ }
      finally { setSearchLoading(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [userSearch, selectedUsers]);

  const addUser = (user) => {
    setSelectedUsers(prev => [...prev, user]);
    setUserSearch("");
    setUserResults([]);
  };

  const removeUser = (userId) => {
    setSelectedUsers(prev => prev.filter(u => u.userId !== userId));
  };

  // ── Preview ───────────────────────────────────────────────
  const selectedType = TYPES.find(t => t.value === form.type) || TYPES[0];

  // ── Send ──────────────────────────────────────────────────
  const handleSend = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    if (form.targetType === "selected" && selectedUsers.length === 0) return;

    setSending(true);
    try {
      let target;
      if (form.targetType === "all") {
        target = { type: "all" };
      } else if (form.targetType === "selected") {
        target = { type: "selected", userIds: selectedUsers.map(u => u.userId) };
      } else if (form.targetType === "kyc") {
        target = { type: "filter", kycStatus: "approved" };
      }

      const res = await sendAdminNotification({
        title:        form.title.trim(),
        body:         form.body.trim(),
        type:         form.type,
        click_action: form.click_action || "/",
        target,
      });

      if (res.status) {
        setSent(res.data);
        setForm({ title: "", body: "", type: "system", click_action: "/", targetType: "all" });
        setSelectedUsers([]);
        setTimeout(() => setSent(null), 4000);
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  const canSend = form.title.trim() && form.body.trim() &&
    (form.targetType !== "selected" || selectedUsers.length > 0);

  return (
    <div className="space-y-5">
      {/* Tab bar */}
      <div className="flex gap-1 bg-[#161616] border border-white/5 rounded-xl p-1 w-fit">
        {[
          { id: "compose", label: "Compose",  icon: "ri-send-plane-line" },
          { id: "history", label: "History",  icon: "ri-history-line" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? "bg-[#00C896]/15 text-[#00C896]" : "text-white/40 hover:text-white/70"
            }`}>
            <i className={`${t.icon} text-sm`} />
            {t.label}
          </button>
        ))}
      </div>

      {/* ── COMPOSE TAB ── */}
      {tab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Left — form */}
          <div className="space-y-4">

            {/* Success banner */}
            {sent && (
              <div className="flex items-center gap-3 px-4 py-3 bg-[#00C896]/10 border border-[#00C896]/20 rounded-xl">
                <i className="ri-checkbox-circle-line text-[#00C896] text-lg" />
                <p className="text-sm text-[#00C896] font-medium">
                  ✓ "{sent.title}" sent to {sent.sent} user{sent.sent !== 1 ? "s" : ""}
                </p>
              </div>
            )}

            {/* Notification type */}
            <div className="bg-[#161616] border border-white/5 rounded-xl p-5">
              <p className="text-xs text-white/40 font-semibold uppercase tracking-wide mb-3">Type</p>
              <div className="grid grid-cols-2 gap-2">
                {TYPES.map(t => (
                  <button key={t.value} onClick={() => setForm(f => ({ ...f, type: t.value }))}
                    className={`flex items-start gap-2 p-2.5 rounded-xl border text-left transition-all ${
                      form.type === t.value
                        ? "border-[#00C896]/40 bg-[#00C896]/10"
                        : "border-white/5 bg-white/2 hover:bg-white/5"
                    }`}>
                    <span className="text-base leading-none mt-0.5">{t.label.split(" ")[0]}</span>
                    <div>
                      <p className={`text-xs font-semibold ${form.type === t.value ? "text-[#00C896]" : "text-white/70"}`}>
                        {t.label.split(" ").slice(1).join(" ")}
                      </p>
                      <p className="text-[10px] text-white/25 mt-0.5">{t.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Title + body */}
            <div className="bg-[#161616] border border-white/5 rounded-xl p-5 space-y-4">
              <p className="text-xs text-white/40 font-semibold uppercase tracking-wide">Message</p>

              <div>
                <label className="text-xs text-white/40 block mb-1.5">Title</label>
                <input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Your deposit was confirmed"
                  maxLength={80}
                  className="w-full bg-[#0f0f0f] border border-white/8 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40"
                />
                <p className="text-[10px] text-white/20 text-right mt-1">{form.title.length}/80</p>
              </div>

              <div>
                <label className="text-xs text-white/40 block mb-1.5">Body</label>
                <textarea
                  value={form.body}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  placeholder="Write your message here..."
                  rows={3}
                  maxLength={200}
                  className="w-full bg-[#0f0f0f] border border-white/8 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40 resize-none"
                />
                <p className="text-[10px] text-white/20 text-right mt-1">{form.body.length}/200</p>
              </div>

              <div>
                <label className="text-xs text-white/40 block mb-1.5">
                  Tap destination <span className="text-white/20">(optional)</span>
                </label>
                <input
                  value={form.click_action}
                  onChange={e => setForm(f => ({ ...f, click_action: e.target.value }))}
                  placeholder="/wallet, /rewards, /bills..."
                  className="w-full bg-[#0f0f0f] border border-white/8 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 font-mono focus:outline-none focus:border-[#00C896]/40"
                />
              </div>
            </div>

            {/* Target audience */}
            <div className="bg-[#161616] border border-white/5 rounded-xl p-5 space-y-4">
              <p className="text-xs text-white/40 font-semibold uppercase tracking-wide">Recipients</p>

              <div className="flex gap-2">
                {TARGETS.map(t => (
                  <button key={t.value} onClick={() => { setForm(f => ({ ...f, targetType: t.value })); setSelectedUsers([]); }}
                    className={`flex-1 flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-center transition-all ${
                      form.targetType === t.value
                        ? "border-[#00C896]/40 bg-[#00C896]/10"
                        : "border-white/5 hover:bg-white/5"
                    }`}>
                    <i className={`${t.icon} text-base`}
                      style={{ color: form.targetType === t.value ? "#00C896" : "rgba(255,255,255,0.3)" }} />
                    <p className={`text-[10px] font-semibold leading-tight ${
                      form.targetType === t.value ? "text-[#00C896]" : "text-white/30"
                    }`}>{t.label}</p>
                  </button>
                ))}
              </div>

              {/* Selected users picker */}
              {form.targetType === "selected" && (
                <div className="space-y-2" ref={searchRef}>
                  {/* Tags */}
                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedUsers.map(u => (
                        <UserTag key={u.userId} user={u} onRemove={removeUser} />
                      ))}
                    </div>
                  )}
                  {/* Search input */}
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                    <input
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      placeholder="Search by name or email..."
                      className="w-full bg-[#0f0f0f] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40"
                    />
                    {searchLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-[#00C896]/30 border-t-[#00C896] rounded-full animate-spin" />
                    )}
                  </div>
                  {/* Dropdown results */}
                  {userResults.length > 0 && (
                    <div className="bg-[#0f0f0f] border border-white/8 rounded-xl overflow-hidden">
                      {userResults.map(u => (
                        <button key={u.userId} onClick={() => addUser(u)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 text-left transition-colors">
                          <div className="w-7 h-7 rounded-full bg-[#00C896]/10 flex items-center justify-center text-[#00C896] text-xs font-bold flex-shrink-0">
                            {(u.userName || u.email).charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-white text-xs font-medium truncate">{u.userName}</p>
                            <p className="text-white/30 text-[10px] truncate">{u.email}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedUsers.length === 0 && (
                    <p className="text-xs text-white/25">Search and select at least one user</p>
                  )}
                </div>
              )}
            </div>

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={!canSend || sending}
              className="w-full py-3.5 rounded-xl font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ background: canSend ? "#00C896" : "rgba(255,255,255,0.05)", color: canSend ? "#000" : "rgba(255,255,255,0.2)" }}
            >
              {sending ? (
                <><div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />Sending…</>
              ) : (
                <><i className="ri-send-plane-fill" />
                  Send to {form.targetType === "all" ? "All Users"
                    : form.targetType === "kyc" ? "KYC Users"
                    : `${selectedUsers.length} User${selectedUsers.length !== 1 ? "s" : ""}`}
                </>
              )}
            </button>
          </div>

          {/* Right — live preview */}
          <div className="space-y-4">
            <div className="bg-[#161616] border border-white/5 rounded-xl p-5">
              <p className="text-xs text-white/40 font-semibold uppercase tracking-wide mb-4">Preview</p>

              {/* Phone mockup */}
              <div className="bg-[#0f0f0f] rounded-2xl p-4 border border-white/5">
                <p className="text-[10px] text-white/20 mb-3 text-center font-medium">Notification panel</p>

                <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5 flex items-start gap-3">
                  {/* Icon */}
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 bg-emerald-900/50">
                    <span className="text-base">{selectedType.label.split(" ")[0]}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">
                      {form.title || <span className="text-white/20">Your notification title</span>}
                    </p>
                    <p className="text-white/40 text-xs mt-0.5 line-clamp-2 leading-relaxed">
                      {form.body || <span>Your notification body will appear here...</span>}
                    </p>
                    <p className="text-white/20 text-[10px] mt-1">Just now</p>
                  </div>
                </div>

                {/* Push notification mockup */}
                <div className="mt-3 bg-white/5 rounded-xl px-3 py-2.5 border border-white/5">
                  <p className="text-[10px] text-white/20 mb-2 font-medium">Push notification</p>
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-md bg-emerald-600 flex items-center justify-center flex-shrink-0">
                      <i className="ri-leaf-line text-white text-[10px]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-[11px] font-semibold truncate">
                        {form.title || "Notification Title"}
                      </p>
                      <p className="text-white/40 text-[10px] line-clamp-1">
                        {form.body || "Notification body..."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-4 space-y-2">
                {[
                  { label: "Type",       value: selectedType.label },
                  { label: "Navigates to", value: form.click_action || "/" },
                  { label: "Recipients", value: form.targetType === "all" ? "All users"
                      : form.targetType === "kyc" ? "KYC approved users"
                      : selectedUsers.length > 0 ? `${selectedUsers.length} selected user${selectedUsers.length > 1 ? "s" : ""}`
                      : "No users selected yet" },
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-center text-xs">
                    <span className="text-white/30">{row.label}</span>
                    <span className="text-white/60 font-medium">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === "history" && (
        <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <p className="text-sm font-semibold text-white">Sent Notifications</p>
            <button onClick={fetchHistory} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
              <i className={`ri-refresh-line text-white/40 text-sm ${histLoading ? "animate-spin" : ""}`} />
            </button>
          </div>

          {histLoading ? (
            <div className="divide-y divide-white/4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4">
                  <div className="h-3 w-48 bg-white/5 rounded animate-pulse" />
                  <div className="h-2.5 w-24 bg-white/5 rounded animate-pulse ml-auto" />
                </div>
              ))}
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mb-3">
                <i className="ri-notification-off-line text-white/20 text-xl" />
              </div>
              <p className="text-white/30 text-sm">No notifications sent yet</p>
            </div>
          ) : (
            <div className="divide-y divide-white/4">
              {history.map((item, i) => (
                <div key={i} className="flex items-start justify-between px-5 py-4 gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium truncate">{item.title}</p>
                    <p className="text-white/30 text-xs mt-0.5 truncate">{item.body}</p>
                    <p className="text-white/20 text-[10px] mt-1.5">
                      {new Date(item.sentAt).toLocaleDateString("en-NG", {
                        day: "numeric", month: "short", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-white/60 text-sm font-bold">{item.total}</p>
                    <p className="text-white/25 text-[10px]">sent</p>
                    <p className="text-[#00C896] text-[10px] mt-0.5 font-medium">
                      {item.readCount} read
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
